
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'

// module components
import EmployeesActions from '@/modules/UsersModule/components/EmployeesActions/index.vue'
import EmployeesFilters from '@/modules/UsersModule/components/EmployeesFilters/index.vue'

// types
import {
    EmployeePayloadT,
    EmployeeT,
    UserDictionaryT,
    UsersGetEmployeesFiltersT,
} from '@/core/types/users.types'

// realisations
import { users } from '@/core/realisations/users.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// composable
import useActions from '@/common/composable/useActions'
import { useMaska } from '@/common/composable/useMaska'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

// constants
import { EmployeesColumns } from '@/core/constants/columns/Users.columns'
import { EMPLOYEES_OBJECT_ROWS } from '@/core/constants/users.constants'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        FailedFetch,
        EmployeesActions,
        TheTable,
        TableScrollWrapper,
        EmployeesFilters,
    },
    setup() {
        const { actions_state, startCreate, startUpdate, startDelete } = useActions<EmployeeT>()
        const notifications = useNotifications()
        const { havePermissions } = useAccount()

        const filters_state = reactive({
            search: null as string | null,
            organizationId: null as number | null,
            departmentId: null as number | null,
            cityId: null as number | null,
            subdivisionId: null as number | null,
            dateOfReceiptFrom: null as string | null,
            dateOfReceiptTo: null as string | null,
            dateOfBirthFrom: null as string | null,
            dateOfBirthTo: null as string | null,
            jobId: null as number | null,
            email: null as string | null,
            phoneNumber: null as string | null,
            workPhone: null as string | null,
            statusId: null as number | null,
        })

        const data_state = reactive({
            employees: new DataState<EmployeeT>({}),
        })

        const { unmaskNumbers } = useMaska()

        //

        const loadEmployees = async (page: number) => {
            data_state.employees.pagination.current_page = page

            try {
                data_state.employees.is_loading = true

                const params = {
                    page,
                    ...filters_state,
                    phoneNumber: filters_state.phoneNumber
                        ? unmaskNumbers(filters_state.phoneNumber)
                        : null,
                    workPhone: filters_state.workPhone
                        ? unmaskNumbers(filters_state.workPhone)
                        : null,
                }

                const response = await users.getEmployees(params)

                data_state.employees.list = response.data

                data_state.employees.pagination.total_pages = response.meta.last_page

                data_state.employees.is_failed = false
                data_state.employees.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.employees.failure.catchError(error)

                if (!data_state.employees.failure.error_state.is_cancel) {
                    data_state.employees.is_failed = true
                }
            } finally {
                if (!data_state.employees.failure.error_state.is_cancel) {
                    data_state.employees.is_loading = false
                }
            }
        }

        //

        onBeforeMount(() => {
            loadEmployees(1)
        })

        const createEmployee = async (payload: EmployeePayloadT) => {
            try {
                actions_state.is_actions_loading = true

                await users.createEmployee(payload)
                await loadEmployees(data_state.employees.pagination.current_page)

                actions_state.is_actions_opened = false
                loadEmployees(1)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ title: 'Ошибка при добавлении сотрудника', error })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateEmployee = async ({
            id,
            payload,
        }: {
            id: number
            payload: EmployeePayloadT
        }) => {
            try {
                actions_state.is_actions_loading = true

                await users.updateEmployee(id, payload)
                await loadEmployees(data_state.employees.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ title: 'Ошибка при сохранении сотрудника', error })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const deleteEmployee = async (id: number) => {
            try {
                actions_state.is_actions_loading = true

                await users.deleteEmployee(id)
                await loadEmployees(data_state.employees.pagination.current_page)

                actions_state.is_actions_opened = false
                notifications.notify({ title: 'Сотрудник удален!', type: 'success' })
            } catch (error) {
                notifications.notifyError({ title: 'Ошибка при удалении сотрудника', error })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const isRowObj = (value: UserDictionaryT, key: string) => {
            return value && (value.description || value.name) && EMPLOYEES_OBJECT_ROWS.includes(key)
        }

        const getRowObj = (value: UserDictionaryT) => {
            return value.description || value.name || '-'
        }

        //

        const applyFilters = (params: UsersGetEmployeesFiltersT) => {
            filters_state.search = params.search || null
            filters_state.organizationId = params.organizationId || null
            filters_state.departmentId = params.departmentId || null
            filters_state.cityId = params.cityId || null
            filters_state.subdivisionId = params.subdivisionId || null
            filters_state.dateOfReceiptFrom = params.dateOfReceiptFrom || null
            filters_state.dateOfReceiptTo = params.dateOfReceiptTo || null
            filters_state.dateOfBirthFrom = params.dateOfBirthFrom || null
            filters_state.dateOfBirthTo = params.dateOfBirthTo || null
            filters_state.jobId = params.jobId || null
            filters_state.email = params.email || null
            filters_state.phoneNumber = params.phoneNumber || null
            filters_state.statusId = params.statusId || null

            loadEmployees(1)
        }

        const resetFilters = () => {
            filters_state.search = null
            filters_state.organizationId = null
            filters_state.departmentId = null
            filters_state.cityId = null
            filters_state.subdivisionId = null
            filters_state.dateOfReceiptFrom = null
            filters_state.dateOfReceiptTo = null
            filters_state.dateOfBirthFrom = null
            filters_state.dateOfBirthTo = null
            filters_state.jobId = null
            filters_state.email = null
            filters_state.phoneNumber = null
            filters_state.statusId = null

            loadEmployees(1)
        }

        //

        return {
            data_state,
            actions_state,
            //
            loadEmployees,
            //
            startCreate,
            startUpdate,
            startDelete,
            createEmployee,
            updateEmployee,
            deleteEmployee,
            isRowObj,
            getRowObj,
            applyFilters,
            resetFilters,
            // constants
            EMPLOYEES_OBJECT_ROWS,
            EmployeesColumns,
            //
            havePermissions,
        }
    },
})
